import { useLocation } from "react-router-dom";

/**
 * Royal Marketing Frame
 * ─────────────────────
 * Screen-wide, pointer-events-none decorative overlay that only appears for
 * the primary marketing account (`user.is_marketing_account === true`) while
 * they're on a game player route. Designed for screen recordings — includes
 * gold border, JackRoyals logo, and the "18+ No Purchase Necessary"
 * compliance disclaimer that must be visible in social clips.
 */
const GAME_ROUTE_PREFIXES = [
  "/game/",
  "/game-play/",
  "/games/",
  "/booming/",
  "/mascot/",
  "/betsoft/",
  "/petersons/",
  "/ka/",
  "/play/",
];

export default function RoyalMarketingFrame({ user }) {
  const location = useLocation();
  if (!user?.is_marketing_account) return null;
  const isGamePage = GAME_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!isGamePage) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[70]"
      data-testid="royal-marketing-frame"
      aria-hidden="true"
    >
      {/* Outer gold double-border */}
      <div className="absolute inset-2 sm:inset-3 rounded-2xl border-[3px] border-amber-400/90 shadow-[0_0_35px_rgba(245,158,11,0.35),inset_0_0_25px_rgba(245,158,11,0.25)]" />
      <div className="absolute inset-[10px] sm:inset-[14px] rounded-xl border border-amber-300/50" />

      {/* Corner ornaments */}
      {["top-2 left-2", "top-2 right-2", "bottom-2 left-2", "bottom-2 right-2"].map((pos) => (
        <div
          key={pos}
          className={`absolute ${pos} w-10 h-10 sm:w-14 sm:h-14`}
        >
          <div className="absolute inset-0 border-t-4 border-l-4 border-amber-300 rounded-tl-2xl"
               style={{
                 transform:
                   pos.includes("right") && pos.includes("top") ? "rotate(90deg)" :
                   pos.includes("right") && pos.includes("bottom") ? "rotate(180deg)" :
                   pos.includes("left") && pos.includes("bottom") ? "rotate(-90deg)" : "none",
               }} />
        </div>
      ))}

      {/* Top-left logo watermark */}
      <div className="absolute top-4 left-6 sm:top-5 sm:left-8 flex items-center gap-2 select-none">
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/40 border-2 border-amber-200">
          <span className="text-black text-sm sm:text-base font-black tracking-tighter">JR</span>
        </div>
        <div className="leading-tight">
          <div className="text-amber-300 font-black text-sm sm:text-base tracking-wide drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
            Jack<span className="text-white">Royals</span>
          </div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-amber-200/80 font-bold">
            Marketing Studio
          </div>
        </div>
      </div>

      {/* Bottom disclaimer bar */}
      <div className="absolute left-6 right-6 sm:left-8 sm:right-8 bottom-3 sm:bottom-4 flex items-center justify-center">
        <div className="px-3 sm:px-5 py-1.5 sm:py-2 rounded-full bg-black/75 border border-amber-400/70 backdrop-blur-sm shadow-lg">
          <span className="text-[10px] sm:text-xs font-bold tracking-wider text-amber-200 uppercase whitespace-nowrap">
            18+ · No Purchase Necessary · Void Where Prohibited · JackRoyals.com
          </span>
        </div>
      </div>
    </div>
  );
}
